import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, ArrowRight, FileSearch } from "lucide-react";

const LINES = [
  { text: "Il debito", color: "" },
  { text: "non è una colpa.", color: "text-brand-blue" },
  { text: "Noi siamo", color: "" },
  { text: "dalla tua parte.", color: "text-brand-aqua" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-gradient-to-b from-brand-mist to-white">
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand-aqua/10 blur-3xl" aria-hidden="true" />
      <div className="container-site py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-aqua/40 px-4 py-2 shadow-sm mb-8"
            data-testid="hero-badge"
          >
            <ShieldCheck className="w-4 h-4 text-brand-aqua" />
            <span className="text-sm font-bold text-brand-ink">
              Assistenza legale e umana — sempre gratuita
            </span>
          </motion.div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-brand-ink" data-testid="hero-title">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.color}`}
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl"
            data-testid="hero-subtitle"
          >
            Il debito è un problema che si può risolvere. Informiamo gratuitamente chi è in
            difficoltà economica sui propri diritti e, nei casi più gravi, offriamo anche supporto
            psicologico con professionisti volontari.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/assistenza" className="btn-primary" data-testid="hero-cta-assistenza">
              Richiedi assistenza gratuita
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/assistenza?tab=stato" className="btn-outline" data-testid="hero-cta-stato">
              <FileSearch className="w-5 h-5" />
              Verifica la tua pratica
            </Link>
          </motion.div>

          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-lg"
            data-testid="hero-stats"
          >
            {[
              ["0 €", "costo per te"],
              ["2", "sedi: Milano e Gallarate"],
              ["100%", "gratuito e riservato"],
            ].map(([num, label]) => (
              <div key={label}>
                <dt className="text-3xl font-extrabold text-brand-blue">{num}</dt>
                <dd className="text-sm font-semibold text-slate-500 mt-1 leading-snug">{label}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2rem] overflow-hidden border-8 border-white shadow-2xl shadow-brand-blue/15"
          >
            <motion.img
              src="https://images.unsplash.com/photo-1604881991720-f91add269bed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxoYW5kcyUyMGhvbGRpbmclMjBzdXBwb3J0JTIwY29tcGFzc2lvbiUyMHdhcm10aHxlbnwwfHx8fDE3ODg2OTM3MDJ8MA&ixlib=rb-4.1.0&q=85"
              alt="Due persone che si tengono per mano a un tavolo, in segno di sostegno"
              className="w-full h-[420px] object-cover"
              style={{ y, scale: 1.15 }}
              data-testid="hero-image"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.2 }}
            className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl shadow-brand-ink/10 border border-slate-100 px-5 py-4 flex items-center gap-3 animate-float-slow"
            data-testid="hero-floating-card"
          >
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-aqua opacity-60" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-aqua" />
            </span>
            <div>
              <p className="font-bold text-brand-ink text-sm">Sportello gratuito attivo</p>
              <p className="text-xs text-slate-500">Milano · Gallarate · online</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
